import {
  CircuitArchiveSchema,
  PublicSnapshotSchema,
  type CircuitArchive,
  type PublicSnapshot,
} from "@paddock-signal/contracts";

const storageKey = "ps-snapshot-cache-v1";
const snapshotPath = "/v1/snapshot";
const circuitArchivePath = "/v1/circuit-results";

interface StoredSnapshot {
  etag: string | null;
  snapshot: PublicSnapshot;
}

export interface SnapshotLoadResult {
  snapshot: PublicSnapshot;
  source: "cache" | "fixture" | "network";
  state: "fresh" | "stale";
  message?: string;
}

export interface SnapshotRepositoryOptions {
  apiOrigin?: string;
  fallback: PublicSnapshot;
  fetch?: typeof fetch;
  now?: () => Date;
  storage?: Pick<Storage, "getItem" | "setItem">;
}

function apiEndpoint(apiOrigin: string | undefined, path: string): string {
  if (apiOrigin === undefined || apiOrigin.length === 0) return path;
  const origin = new URL(apiOrigin);
  const localHttp =
    origin.protocol === "http:" &&
    (origin.hostname === "localhost" || origin.hostname === "127.0.0.1");
  if (
    (origin.protocol !== "https:" && !localHttp) ||
    origin.username.length > 0 ||
    origin.password.length > 0 ||
    origin.pathname !== "/" ||
    origin.search.length > 0 ||
    origin.hash.length > 0
  ) {
    throw new TypeError("Snapshot API origin must be an HTTPS origin.");
  }
  return new URL(path, origin).toString();
}

function snapshotEndpoint(apiOrigin: string | undefined): string {
  return apiEndpoint(apiOrigin, snapshotPath);
}

function circuitArchiveEndpoint(
  apiOrigin: string | undefined,
  circuitId: string,
): string {
  if (!/^[a-z0-9-]+$/.test(circuitId)) {
    throw new TypeError("Circuit ID is invalid.");
  }
  return apiEndpoint(
    apiOrigin,
    `${circuitArchivePath}/${encodeURIComponent(circuitId)}`,
  );
}

export async function loadCircuitArchive(
  apiOrigin: string | undefined,
  circuitId: string,
  fetcher: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<CircuitArchive | null> {
  const response = await fetcher(circuitArchiveEndpoint(apiOrigin, circuitId), {
    headers: { Accept: "application/json" },
    signal: AbortSignal.timeout(8_000),
  });
  if (response.status === 404 || response.status === 503) return null;
  if (!response.ok) {
    throw new TypeError(
      `Circuit archive returned HTTP ${String(response.status)}.`,
    );
  }
  if (!response.headers.get("content-type")?.includes("application/json")) {
    throw new TypeError("Circuit archive response must be JSON.");
  }
  return CircuitArchiveSchema.parse(await response.json());
}

export interface FeedbackSubmission {
  category: "issue" | "error" | "idea";
  details: string;
  diagnostics?: string;
  snapshotId: string;
}

export async function submitFeedback(
  apiOrigin: string | undefined,
  payload: FeedbackSubmission,
  fetcher: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<void> {
  const endpoint = apiEndpoint(apiOrigin, "/v1/feedback");
  const response = await fetcher(endpoint, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    signal: AbortSignal.timeout(8_000),
  });
  if (!response.ok) {
    throw new TypeError(`Feedback returned HTTP ${String(response.status)}.`);
  }
}

function parseStoredSnapshot(value: string | null): StoredSnapshot | null {
  if (value === null) return null;
  try {
    const parsed = JSON.parse(value) as {
      etag?: unknown;
      snapshot?: unknown;
    };
    const etag =
      parsed.etag === null || typeof parsed.etag === "string"
        ? parsed.etag
        : null;
    return {
      etag,
      snapshot: PublicSnapshotSchema.parse(parsed.snapshot),
    };
  } catch {
    return null;
  }
}

function readStoredSnapshot(
  storage: Pick<Storage, "getItem" | "setItem"> | undefined,
): StoredSnapshot | null {
  try {
    return parseStoredSnapshot(storage?.getItem(storageKey) ?? null);
  } catch {
    return null;
  }
}

function freshness(snapshot: PublicSnapshot, now: Date): "fresh" | "stale" {
  return Date.parse(snapshot.staleAt) <= now.getTime() ? "stale" : "fresh";
}

function normalizedEtag(value: string | null): string | null {
  if (value === null || value.length > 200 || /[\r\n]/.test(value)) return null;
  return value;
}

export class SnapshotRepository {
  private readonly fallback: PublicSnapshot;
  private readonly fetcher: typeof fetch;
  private readonly now: () => Date;
  private readonly endpoint: string;
  private readonly storage: Pick<Storage, "getItem" | "setItem"> | undefined;
  private current: StoredSnapshot | null;

  constructor(options: SnapshotRepositoryOptions) {
    this.fallback = PublicSnapshotSchema.parse(options.fallback);
    this.endpoint = snapshotEndpoint(options.apiOrigin);
    this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.now = options.now ?? (() => new Date());
    this.storage = options.storage;
    this.current = readStoredSnapshot(this.storage);
  }

  fixture(): SnapshotLoadResult {
    return {
      snapshot: this.fallback,
      source: "fixture",
      state: "fresh",
    };
  }

  async load(): Promise<SnapshotLoadResult> {
    const previous = this.current?.snapshot ?? this.fallback;
    const headers = new Headers({ Accept: "application/json" });
    if (this.current?.etag) headers.set("If-None-Match", this.current.etag);
    try {
      const response = await this.fetcher(this.endpoint, {
        headers,
        signal: AbortSignal.timeout(8_000),
      });
      if (response.status === 304) {
        if (this.current === null) {
          throw new TypeError("A 304 response requires a cached snapshot.");
        }
        return {
          snapshot: this.current.snapshot,
          source: "cache",
          state: freshness(this.current.snapshot, this.now()),
        };
      }
      if (!response.ok) {
        throw new TypeError(
          `Snapshot returned HTTP ${String(response.status)}.`,
        );
      }
      if (!response.headers.get("content-type")?.includes("application/json")) {
        throw new TypeError("Snapshot response must be JSON.");
      }
      const snapshot = PublicSnapshotSchema.parse(await response.json());
      this.current = {
        snapshot,
        etag: normalizedEtag(response.headers.get("etag")),
      };
      try {
        this.storage?.setItem(storageKey, JSON.stringify(this.current));
      } catch {
        return {
          snapshot,
          source: "network",
          state: freshness(snapshot, this.now()),
        };
      }
      return {
        snapshot,
        source: "network",
        state: freshness(snapshot, this.now()),
      };
    } catch (error) {
      return {
        snapshot: previous,
        source: this.current === null ? "fixture" : "cache",
        state: "stale",
        message:
          error instanceof Error
            ? error.message
            : "Snapshot refresh was unavailable.",
      };
    }
  }
}
